import { lang, t } from './i18n/index.ts';
import { isCar } from './ui/surface.ts';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public detail: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

/** Oturum sunucuda geçersizleşti (çerez doldu, cihaz başka yerden çıkarıldı).
 *  Dinleyenler: yönlendirici (girişe götürür) ve oynatıcı (kapanır). */
export const UNAUTHORIZED_EVENT = 'akis:unauthorized';

async function request<T>(
  method: string, path: string, body?: unknown, signal?: AbortSignal, timeoutMs = 0,
): Promise<T> {
  // İsteğe bağlı zaman aşımı: yanıt vermeyen bir bağlantı (tünel, kara delik)
  // çağıranı tarayıcının kendi zaman aşımına kadar bekletmesin.
  let timedOut = false;
  let timer: number | null = null;
  let effectiveSignal = signal;
  if (timeoutMs > 0 && typeof AbortController === 'function') {
    const controller = new AbortController();
    effectiveSignal = controller.signal;
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener('abort', () => controller.abort(), { once: true });
    }
    timer = window.setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
  }
  try {
    return await send<T>(method, path, body, effectiveSignal);
  } catch (err) {
    if (timedOut) throw new ApiError(0, 'network', t('common.network'));
    throw err;
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

async function send<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const init: RequestInit = {
    method,
    signal,
    // Sunucu hata mesajlarını ve paket metinlerini bu dilde döndürür.
    // Cihaz adı KODLANARAK gider: HTTP başlığı Latin-1 dışı harf taşıyamaz ve
    // fetch isteği hiç göndermeden TypeError fırlatır. "Araç ekranı"ndaki "ı"
    // yüzünden Tesla'da Türkçe arayüzle tek bir istek bile çıkmıyor, her düğme
    // "Bağlantı kurulamadı" diyordu.
    headers: { 'X-Device-Name': encodeURIComponent(deviceLabel()), 'X-Lang': lang() },
    credentials: 'same-origin',
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
  }
  let response: Response;
  try {
    response = await fetch(path, init);
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'network', t('common.network'));
  }
  if (response.status === 204) return undefined as T;
  let text = '';
  try {
    text = await response.text();
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'network', t('common.network'));
  }
  let data: any = {};
  if (text) {
    // JSON olmayan gövde (ör. dağıtım sırasında vekil sunucunun 502 sayfası)
    // kullanıcıya ham HTML olarak gösterilmez; genel hata metnine düşülür.
    try { data = JSON.parse(text); } catch { data = {}; }
  }
  if (data === null || typeof data !== 'object') data = {};
  if (!response.ok) {
    // Giriş/kayıt uçlarının 401'i "yanlış parola" demektir; oturum düşmesi değil.
    if (response.status === 401 && !path.startsWith('/api/auth/')) {
      try { window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT)); } catch { /* olay yok */ }
    }
    throw new ApiError(response.status, data.code ?? 'error', data.error ?? t('common.error'), data);
  }
  return data as T;
}

function deviceLabel(): string {
  const ua = navigator.userAgent.toLowerCase();
  if (isCar()) return t('device.car');
  if (/\b(smart-tv|smarttv|tizen|webos|hbbtv)\b/.test(ua)) return t('device.tv');
  if (/\bipad|tablet\b/.test(ua)) return t('device.tablet');
  if (/\bandroid|iphone|mobile\b/.test(ua)) return t('device.phone');
  return t('device.desktop');
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal, timeoutMs = 0) =>
    request<T>('POST', path, body ?? {}, signal, timeoutMs),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  deviceLabel,
};

// --- Sunucudan dönen biçimler -------------------------------------------------

export interface Quota {
  limitSeconds: number | null;
  usedSeconds: number;
  remainingSeconds: number | null;
  exhausted: boolean;
  resetsAt: number;
  timezone: string;
}

export interface Entitlements {
  planCode: string;
  effectivePlan: string;
  planName: string;
  expired: boolean;
  renewsAt: number | null;
  cancelAtPeriodEnd: boolean;
  /** Sağlayıcıda iptal edilen abonelik geri açılamaz. */
  canResume?: boolean;
  iptv: boolean;
  sourceSlots: number;
  concurrentStreams: number;
  dailySeconds: number | null;
  monthlyHoursCap: number | null;
  maxHeight: number | null;
  addons: string[];
  introOfferUsed: boolean;
}

/** Abonelik özeti (sunucu: billing/summary.ts). Elle atanmış pakette `status` null. */
export interface SubscriptionSummary {
  status: 'active' | 'unpaid' | 'canceled' | null;
  period: 'monthly' | 'yearly' | null;
  priceMinor: number | null;
  /** "120,00 TL/ay" */
  priceLabel: string | null;
  /** Deneme süresinde: tahsilat henüz yapılmadı. */
  inTrial: boolean;
  nextChargeAt: number | null;
  /** Erişimin sona ereceği an (iptal, ödenmemiş yenileme ya da süreli atama). */
  endsAt: number | null;
  paymentFailed: boolean;
  /** Sonraki dönemde geçilecek paket (alt pakete geçiş dönem sonunda uygulanır). */
  pendingPlan?: { code: string; name: string } | null;
  canChangePlan?: boolean;
  canUpdateCard?: boolean;
}

/** İşlem geçmişi satırı (`/api/billing/history`). */
export interface BillingHistoryEntry {
  id: string;
  ref: string;
  type: string;
  kind: 'trial_started' | 'purchase' | 'renewal' | 'payment_failed' | 'cancelled' | 'resumed' | 'plan_changed' | 'card_updated' | 'admin' | 'other';
  plan_code: string | null;
  plan_name: string;
  amount_minor: number;
  amount_label: string;
  provider: string;
  period: 'monthly' | 'yearly' | null;
  trial: boolean;
  test: boolean;
  created_at: number;
}

export interface ActiveStream {
  id: string;
  title: string;
  deviceName: string;
  kind: string;
  startedAt: number;
}

export interface SessionPayload {
  user: { id: string; email: string; displayName: string; createdAt: number };
  entitlements: Entitlements;
  /** Eski sunucu sürümünde yoktur; arayüz yokluğunu kaldırır. */
  subscription?: SubscriptionSummary;
  quota: Quota;
  activeStream: ActiveStream | null;
  brand: string;
  isAdmin?: boolean;
}

export interface YoutubeItem {
  id: string;
  title: string;
  channel: string;
  duration: number | null;
  live: boolean;
  thumbnail: string;
}

export interface CatalogItem {
  id: string;
  itemKey: string;
  kind: string;
  title: string;
  category: string;
  logo: string | null;
  sourceId: string;
  seriesId: string | null;
  season: number | null;
  episode: number | null;
  epgChannelId: string | null;
  favorite: boolean;
  extra: Record<string, unknown>;
  /** Yalnız dizi bölümlerinde: kaldığın yer. Sunucu ancak 15 saniyeden
   *  fazlası izlenmişse gönderir. */
  progress?: { positionSec: number; durationSec: number };
}

export interface EpgProgram { start: number; stop: number; title: string; description?: string }
export interface NowNext { now: EpgProgram | null; next: EpgProgram | null; fetchedAt: number | null }

export interface PlaybackStart {
  sessionId: string;
  mode: 'live' | 'vod';
  kind: string;
  itemKey: string;
  title: string;
  durationSec: number | null;
  startPositionSec: number;
  videoPath: string;
  audioPath: string;
  /** Ayrı yayın adresi ve o oynatmaya özel akış anahtarı; tanımlı değilse akış site içinden gelir. */
  media?: { base: string; key: string } | null;
  leaseSeconds: number;
  quota: Quota;
  meta: Record<string, unknown>;
}
